/**
 * The tracker's unsaved-edit state (plan §3.1, §7) — pure functions only, so every rule is unit-tested without a
 * DOM: staged edits layered over the saved cells, Undo where **one user action is one step** (a typed cell, a
 * whole paste, a comment apply, a clear), Cancel, the rectangular selection and its total.
 *
 * Nothing here knows about the server. A saved value never appears in `edits`, so Undo can never reach past a
 * save; Cancel simply drops every edit and the grid falls back to the saved cells.
 */

/** A numeric KPI holds a number, a Text KPI a string; `null` is a blank — never zero. */
export type CellValue = number | string | null;

export interface CellRecord {
  value: CellValue;
  comment: string | null;
  reason: string | null;
  followUp: string | null;
}

/** What the user changed in one cell. Absent fields fall back to the saved cell. */
export interface CellEdit {
  value?: CellValue;
  comment?: string | null;
  reason?: string | null;
  followUp?: string | null;
  /** The typed text when it could not be read as a value of the KPI's unit; the cell cannot be saved until fixed. */
  invalid?: string;
}

export interface HistoryEntry {
  key: string;
  /** What `edits[key]` held before the step; `undefined` = no edit (Undo removes the key). */
  previous: CellEdit | undefined;
}

export type HistoryStep = readonly HistoryEntry[];

export interface EditState {
  edits: Readonly<Record<string, CellEdit>>;
  history: readonly HistoryStep[];
}

export const EMPTY_EDIT_STATE: EditState = { edits: {}, history: [] };

export const BLANK_RECORD: CellRecord = { value: null, comment: null, reason: null, followUp: null };

/** `${kpiId}:${weekId}` — the key of the server's `values` map too. */
export const cellKey = (kpiId: string, weekId: string): string => `${kpiId}:${weekId}`;

export const splitCellKey = (key: string): { kpiId: string; weekId: string } => {
  const separator = key.indexOf(':');

  return { kpiId: key.slice(0, separator), weekId: key.slice(separator + 1) };
};

const NOTE_FIELDS = ['comment', 'reason', 'followUp'] as const;

/** The saved cell with the unsaved edit on top. */
export const effectiveRecord = (base: CellRecord | null, edit: CellEdit | undefined): CellRecord => {
  const saved = base ?? BLANK_RECORD;

  if (!edit) {
    return saved;
  }

  return {
    value: edit.value === undefined ? saved.value : edit.value,
    comment: edit.comment === undefined ? saved.comment : edit.comment,
    reason: edit.reason === undefined ? saved.reason : edit.reason,
    followUp: edit.followUp === undefined ? saved.followUp : edit.followUp,
  };
};

const sameRecord = (a: CellRecord, b: CellRecord): boolean =>
  a.value === b.value && NOTE_FIELDS.every((field) => (a[field] ?? null) === (b[field] ?? null));

/**
 * Merges `patch` into a cell's edit. A valid value clears a previous invalid entry; an edit that brings the cell
 * back to exactly its saved state is dropped, so the unsaved count only counts real changes.
 */
const mergeEdit = (current: CellEdit | undefined, patch: CellEdit, base: CellRecord | null): CellEdit | undefined => {
  const merged: CellEdit = { ...current, ...patch };

  if (patch.invalid === undefined && patch.value !== undefined) {
    delete merged.invalid;
  }

  if (merged.invalid === undefined && sameRecord(effectiveRecord(base, merged), base ?? BLANK_RECORD)) {
    return undefined;
  }

  return merged;
};

export interface CellWrite {
  key: string;
  patch: CellEdit;
  /** The saved cell, `null` when blank. */
  base: CellRecord | null;
}

/** Applies several cell writes as **one** Undo step. A step that touches nothing is not recorded. */
export const applyWrites = (state: EditState, writes: readonly CellWrite[]): EditState => {
  if (writes.length === 0) {
    return state;
  }

  const edits: Record<string, CellEdit> = { ...state.edits };
  const step: HistoryEntry[] = [];
  const touched = new Set<string>();

  for (const { key, patch, base } of writes) {
    if (!touched.has(key)) {
      step.push({ key, previous: state.edits[key] });
      touched.add(key);
    }

    const next = mergeEdit(edits[key], patch, base);

    if (next) {
      edits[key] = next;
    } else {
      delete edits[key];
    }
  }

  return { edits, history: [...state.history, step] };
};

/** One cell typed, cleared or annotated — one step. */
export const applyEdit = (state: EditState, key: string, patch: CellEdit, base: CellRecord | null): EditState =>
  applyWrites(state, [{ key, patch, base }]);

/** A whole pasted block — one step, however many cells it filled. */
export const applyPaste = (state: EditState, writes: readonly CellWrite[]): EditState => applyWrites(state, writes);

/** Reverts the most recent step; a no-op with no history. Never touches anything outside that step. */
export const undo = (state: EditState): EditState => {
  const step = state.history[state.history.length - 1];

  if (!step) {
    return state;
  }

  const edits: Record<string, CellEdit> = { ...state.edits };

  for (const { key, previous } of step) {
    if (previous === undefined) {
      delete edits[key];
    } else {
      edits[key] = previous;
    }
  }

  return { edits, history: state.history.slice(0, -1) };
};

export const editCount = (state: EditState): number => Object.keys(state.edits).length;

export const invalidKeys = (state: EditState): string[] => Object.keys(state.edits).filter((key) => state.edits[key]?.invalid !== undefined);

/** Splits clipboard text into rows of tab-separated cells (Excel / Sheets copy format); a trailing newline is not a row. */
export const parseClipboard = (text: string): string[][] => {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');

  if (lines.length > 1 && lines[lines.length - 1] === '') {
    lines.pop();
  }

  return lines.map((line) => line.split('\t'));
};

/** True when the text is a block (more than one cell), not a single value. */
export const isMultiCellPaste = (text: string): boolean => /[\t\n\r]/.test(text.replace(/\r?\n$/, ''));

export interface GridPosition {
  row: number;
  col: number;
}

/** The cells a pasted block lands on from `start`, clipped to the grid. */
export const pasteTargets = (block: readonly (readonly string[])[], start: GridPosition, rowCount: number, colCount: number) =>
  block.flatMap((cells, dy) =>
    cells
      .map((raw, dx) => ({ row: start.row + dy, col: start.col + dx, raw }))
      .filter(({ row, col }) => row >= 0 && col >= 0 && row < rowCount && col < colCount),
  );

export interface SelectionBounds {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/** The rectangle between the anchor and the focus; `null` when either is off the grid. */
export const selectionBounds = (anchor: GridPosition | null, focus: GridPosition | null): SelectionBounds | null => {
  if (!anchor || !focus || anchor.row < 0 || anchor.col < 0 || focus.row < 0 || focus.col < 0) {
    return null;
  }

  return {
    top: Math.min(anchor.row, focus.row),
    bottom: Math.max(anchor.row, focus.row),
    left: Math.min(anchor.col, focus.col),
    right: Math.max(anchor.col, focus.col),
  };
};

export const isInSelection = (bounds: SelectionBounds | null, row: number, col: number): boolean =>
  bounds !== null && row >= bounds.top && row <= bounds.bottom && col >= bounds.left && col <= bounds.right;

export const selectionSize = (bounds: SelectionBounds | null): number =>
  bounds ? (bounds.bottom - bounds.top + 1) * (bounds.right - bounds.left + 1) : 0;

export interface SelectionTotal {
  /** `null` when no selected cell holds a number — never 0. */
  sum: number | null;
  /** How many numeric cells were added. */
  count: number;
}

/** Adds the numeric cells of a selection; blanks and text are left out, never counted as 0. */
export const selectionTotal = (values: readonly CellValue[]): SelectionTotal => {
  const numbers = values.filter((value): value is number => typeof value === 'number' && Number.isFinite(value));

  return { sum: numbers.length === 0 ? null : numbers.reduce((sum, value) => sum + value, 0), count: numbers.length };
};

/** A position moved by `delta`, held inside the grid. */
export const movePosition = (position: GridPosition, delta: GridPosition, rowCount: number, colCount: number): GridPosition => ({
  row: Math.min(rowCount - 1, Math.max(0, position.row + delta.row)),
  col: Math.min(colCount - 1, Math.max(0, position.col + delta.col)),
});
